<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';
import Button from 'next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import EventReminderModal from './EventReminderModal.vue';
import EventStageDefaultsModal from './EventStageDefaultsModal.vue';
import EventRemindersAPI from 'dashboard/api/planning/eventReminders';
import EventStatusesAPI from 'dashboard/api/planning/eventStatuses';
import EventsAPI from 'dashboard/api/planning/events';
import StatusColorPicker from './components/StatusColorPicker.vue';
import { getDefaultStageColor } from './useEventStageColors';

const store = useStore();
const { t } = useI18n();
const alertMessage = useAlert;

const showStageDefaultsModal = ref(false);

const records = computed(
  () => store.getters['planningEventStatuses/getRecords'] || []
);
const loading = computed(
  () => store.state.planningEventStatuses?.uiFlags?.isFetching || false
);

const form = reactive({
  name: '',
  anchor_system_stage: 'inquiry',
  active: true,
  color: null,
});
const editingStatusId = ref(null);
const editingSystemStage = ref(null);
const isPopoverOpen = ref(false);
const popoverRef = ref(null);
const showDeleteModal = ref(false);
const selectedStatus = ref(null);
const popoverPosition = reactive({
  top: 0,
  left: 0,
});
const popoverTriggerRect = ref(null);

const showReminderModal = ref(false);
const reminderModalStatus = ref(null);
const reminderCounts = ref({});
const systemColorState = ref({});

const systemStages = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'lost',
  'archived',
];

const reminderCountKey = (type, key) => `${type}:${key}`;

const loadReminderCounts = async () => {
  try {
    const response = await EventRemindersAPI.getAll();
    const counts = {};
    for (const r of response.data || []) {
      const k = reminderCountKey(r.status_type, r.status_key);
      counts[k] = (counts[k] || 0) + 1;
    }
    reminderCounts.value = counts;
  } catch {
    // silently ignore — counts just won't show
  }
};

const reminderCount = (type, key) =>
  reminderCounts.value[reminderCountKey(type, key)] || 0;

const loadSystemColors = async () => {
  try {
    const response = await EventsAPI.statusOptions();
    const map = {};
    (response.data?.system_statuses || []).forEach(status => {
      map[status.key] = {
        color: status.color || null,
        resolved_color:
          status.resolved_color || getDefaultStageColor(status.key),
      };
    });
    systemColorState.value = map;
  } catch {
    systemColorState.value = systemStages.reduce((acc, stage) => {
      acc[stage] = {
        color: null,
        resolved_color: getDefaultStageColor(stage),
      };
      return acc;
    }, {});
  }
};

const systemResolvedColor = stage =>
  systemColorState.value[stage]?.resolved_color || getDefaultStageColor(stage);

const systemStoredColor = stage => systemColorState.value[stage]?.color || null;

const customResolvedColor = status =>
  status.resolved_color ||
  status.color ||
  systemResolvedColor(status.anchor_system_stage);

const STAGE_LABELS = {
  draft: 'PLANNING.EVENTS.STAGES.DRAFT',
  inquiry: 'PLANNING.EVENTS.STAGES.INQUIRY',
  confirmed: 'PLANNING.EVENTS.STAGES.CONFIRMED',
  completed: 'PLANNING.EVENTS.STAGES.COMPLETED',
  lost: 'PLANNING.EVENTS.STAGES.LOST',
  archived: 'PLANNING.EVENTS.STAGES.ARCHIVED',
};

const formatStageLabel = stage => t(STAGE_LABELS[stage]);

const openReminderModal = statusRow => {
  reminderModalStatus.value = statusRow;
  showReminderModal.value = true;
};

const onReminderModalClose = () => {
  showReminderModal.value = false;
  loadReminderCounts();
};

const resetForm = () => {
  form.name = '';
  form.anchor_system_stage = 'inquiry';
  form.active = true;
  form.color = null;
  editingStatusId.value = null;
  editingSystemStage.value = null;
};

const PANEL_WIDTH = 380;
const VIEWPORT_SPACING = 8;

const setPopoverPositionFromTrigger = triggerEl => {
  if (!triggerEl) return;

  const rect = triggerEl.getBoundingClientRect();
  popoverTriggerRect.value = rect;
  popoverPosition.top = rect.bottom + VIEWPORT_SPACING;
  popoverPosition.left = Math.min(
    Math.max(VIEWPORT_SPACING, rect.right - PANEL_WIDTH),
    window.innerWidth - PANEL_WIDTH - VIEWPORT_SPACING
  );
};

const updatePopoverPosition = () => {
  if (!popoverTriggerRect.value || !popoverRef.value) return;

  const triggerRect = popoverTriggerRect.value;
  const panelRect = popoverRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_SPACING;
  const spaceAbove = triggerRect.top - VIEWPORT_SPACING;
  const shouldOpenAbove =
    panelRect.height > spaceBelow && spaceAbove > spaceBelow;

  if (shouldOpenAbove) {
    popoverPosition.top = Math.max(
      VIEWPORT_SPACING,
      triggerRect.top - panelRect.height - VIEWPORT_SPACING
    );
    return;
  }

  popoverPosition.top = Math.min(
    triggerRect.bottom + VIEWPORT_SPACING,
    window.innerHeight - panelRect.height - VIEWPORT_SPACING
  );
};

const openCreateFlapout = async event => {
  if (editingStatusId.value !== null || editingSystemStage.value !== null) {
    resetForm();
  }
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const openEditFlapout = async (status, event) => {
  if (editingStatusId.value !== status.id) {
    form.name = status.name;
    form.anchor_system_stage = status.anchor_system_stage;
    form.active = status.active;
    form.color = status.color || null;
    editingStatusId.value = status.id;
    editingSystemStage.value = null;
  }
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const openEditSystemFlapout = async (stage, event) => {
  if (editingSystemStage.value !== stage) {
    form.color = systemStoredColor(stage);
    editingSystemStage.value = stage;
    editingStatusId.value = null;
  }
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const dismissFlapout = () => {
  isPopoverOpen.value = false;
};

const cancelFlapout = () => {
  isPopoverOpen.value = false;
  resetForm();
};

const saveStatus = async () => {
  if (editingSystemStage.value) {
    try {
      await EventStatusesAPI.updateSystemColor(
        editingSystemStage.value,
        form.color
      );
      await loadSystemColors();
      await store.dispatch('planningEventStatuses/get');
    } catch {
      alertMessage(t('PLANNING.STATUSES.COLOR.SAVE_ERROR'));
      return;
    }
    cancelFlapout();
    return;
  }

  if (!form.name.trim()) return;
  if (editingStatusId.value) {
    await store.dispatch('planningEventStatuses/update', {
      id: editingStatusId.value,
      ...form,
    });
  } else {
    await store.dispatch('planningEventStatuses/create', { ...form });
  }
  cancelFlapout();
};

const openDeleteModal = status => {
  selectedStatus.value = status;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  selectedStatus.value = null;
};

const removeStatus = async () => {
  if (!selectedStatus.value) return;

  await store.dispatch('planningEventStatuses/delete', selectedStatus.value.id);
  closeDeleteModal();
};

const localStatusesByStage = ref(
  systemStages.reduce((acc, stage) => {
    acc[stage] = [];
    return acc;
  }, {})
);

watch(
  records,
  newRecords => {
    const grouped = systemStages.reduce((acc, stage) => {
      acc[stage] = [];
      return acc;
    }, {});
    for (const status of newRecords) {
      if (grouped[status.anchor_system_stage]) {
        grouped[status.anchor_system_stage].push(status);
      }
    }
    for (const stage of systemStages) {
      grouped[stage].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.created_at) - new Date(b.created_at) ||
          a.id - b.id
      );
      localStatusesByStage.value[stage] = grouped[stage];
    }
  },
  { immediate: true, deep: true }
);

const onStageDragEnd = async stage => {
  const items = localStatusesByStage.value[stage];
  if (!items || items.length < 2) return;

  const positionsHash = items.reduce((acc, item, index) => {
    acc[item.id] = index;
    return acc;
  }, {});

  try {
    await store.dispatch('planningEventStatuses/reorder', positionsHash);
  } catch {
    alertMessage(t('PLANNING.STATUSES.REORDER_ERROR'));
  }
};

const collapsedStages = ref(new Set());

const toggleStage = stage => {
  const next = new Set(collapsedStages.value);
  if (next.has(stage)) next.delete(stage);
  else next.add(stage);
  collapsedStages.value = next;
};

const handleFlapoutEscape = e => {
  if (isPopoverOpen.value && e.key === 'Escape') {
    dismissFlapout();
  }
};

onMounted(async () => {
  document.addEventListener('keydown', handleFlapoutEscape);
  await Promise.all([
    store.dispatch('planningEventStatuses/get'),
    loadSystemColors(),
  ]);
  loadReminderCounts();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleFlapoutEscape);
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PLANNING.STATUSES.LOADING')"
    :no-records-found="false"
    :no-records-message="t('PLANNING.STATUSES.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PLANNING.STATUSES.TITLE')"
        :description="t('PLANNING.STATUSES.DESCRIPTION')"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-settings-2"
              md
              slate
              outline
              :label="t('PLANNING.STATUSES.DEFAULTS.CONFIGURE_BUTTON')"
              @click="showStageDefaultsModal = true"
            />
            <Button
              icon="i-lucide-plus"
              md
              :label="t('PLANNING.STATUSES.ADD_STATUS')"
              @click="openCreateFlapout"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th class="py-4 w-8" />
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PLANNING.STATUSES.TABLE.NAME') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11 w-24"
            >
              {{ t('PLANNING.STATUSES.TABLE.ACTIVE') }}
            </th>
            <th
              class="py-4 ltr:text-right rtl:text-left font-semibold text-n-slate-11 w-40"
            >
              {{ t('PLANNING.STATUSES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <template v-for="stage in systemStages" :key="stage">
          <!-- System stage header row in its own tbody -->
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr
              class="border-b border-n-weak"
              :class="
                localStatusesByStage[stage]?.length
                  ? 'cursor-pointer select-none'
                  : ''
              "
              @click="
                localStatusesByStage[stage]?.length ? toggleStage(stage) : null
              "
            >
              <td class="py-4 w-8" />
              <td class="py-4">
                <div class="flex items-center gap-2">
                  <span
                    class="w-5 h-5 flex items-center justify-center shrink-0"
                  >
                    <template v-if="localStatusesByStage[stage]?.length">
                      <svg
                        class="size-3.5 transition-transform"
                        :class="collapsedStages.has(stage) ? '' : 'rotate-90'"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </template>
                  </span>
                  <span
                    class="size-2 rounded-full shrink-0"
                    :style="{ backgroundColor: systemResolvedColor(stage) }"
                  />
                  <span class="font-semibold">{{
                    formatStageLabel(stage)
                  }}</span>
                  <span
                    v-if="localStatusesByStage[stage]?.length"
                    class="text-sm"
                    >{{ localStatusesByStage[stage].length }}</span
                  >
                </div>
              </td>
              <td class="py-4 text-neutral-100 dark:text-neutral-900">—</td>
              <td class="py-4 w-32">
                <div class="flex items-center justify-end gap-2">
                  <span class="inline-flex items-center gap-1">
                    <Button
                      v-tooltip.top="t('PLANNING.STATUSES.CONFIGURE_REMINDERS')"
                      icon="i-lucide-bell"
                      md
                      slate
                      faded
                      @click.stop="
                        openReminderModal({
                          type: 'system',
                          key: stage,
                          name: formatStageLabel(stage),
                        })
                      "
                    />
                    <span
                      v-if="reminderCount('system', stage)"
                      class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11 text-xs font-medium"
                      >{{ reminderCount('system', stage) }}</span
                    >
                  </span>
                  <Button
                    v-tooltip.top="t('PLANNING.STATUSES.EDIT_TOOLTIP')"
                    icon="i-lucide-pen-line"
                    md
                    slate
                    faded
                    @click.stop="openEditSystemFlapout(stage, $event)"
                  />
                  <span class="invisible pointer-events-none"
                    ><Button icon="i-lucide-trash-2" md ruby faded
                  /></span>
                </div>
              </td>
            </tr>
          </tbody>

          <!-- Custom statuses: Draggable renders as <tbody> so <tr> children are valid -->
          <Draggable
            v-if="!collapsedStages.has(stage)"
            v-model="localStatusesByStage[stage]"
            tag="tbody"
            item-key="id"
            handle=".drag-handle"
            ghost-class="opacity-50"
            class="text-n-slate-11"
            @end="onStageDragEnd(stage)"
          >
            <template #item="{ element: status }">
              <tr class="border-b border-n-weak/50 bg-n-solid-1">
                <td class="py-4 w-8" />
                <td class="py-4">
                  <div class="flex items-center gap-2 pl-7">
                    <span
                      class="drag-handle shrink-0 flex items-center justify-center cursor-grab text-n-slate-9 hover:text-n-slate-11"
                    >
                      <i class="i-lucide-grip-vertical size-4" />
                    </span>
                    <span
                      class="size-2 rounded-full shrink-0"
                      :style="{ backgroundColor: customResolvedColor(status) }"
                    />
                    <span>{{ status.name }}</span>
                  </div>
                </td>
                <td class="py-4">
                  <Switch
                    :model-value="status.active"
                    @change="
                      store.dispatch('planningEventStatuses/update', {
                        id: status.id,
                        active: !status.active,
                      })
                    "
                  />
                </td>
                <td class="py-4">
                  <div class="flex items-center justify-end gap-2">
                    <span class="inline-flex items-center gap-1">
                      <Button
                        v-tooltip.top="
                          t('PLANNING.STATUSES.CONFIGURE_REMINDERS')
                        "
                        icon="i-lucide-bell"
                        md
                        slate
                        faded
                        class="!px-2.5"
                        @click="
                          openReminderModal({
                            type: 'custom',
                            key: status.key,
                            name: status.name,
                          })
                        "
                      >
                        <span
                          v-if="reminderCount('custom', status.key)"
                          class="counter"
                          >{{ reminderCount('custom', status.key) }}</span
                        >
                      </Button>
                    </span>
                    <Button
                      v-tooltip.top="t('PLANNING.STATUSES.EDIT_TOOLTIP')"
                      icon="i-lucide-pen-line"
                      md
                      slate
                      faded
                      @click="openEditFlapout(status, $event)"
                    />
                    <Button
                      v-tooltip.top="t('PLANNING.STATUSES.DELETE_TOOLTIP')"
                      icon="i-lucide-trash-2"
                      md
                      ruby
                      faded
                      @click="openDeleteModal(status)"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </Draggable>
        </template>
      </table>
    </template>
  </SettingsLayout>
  <div
    v-show="isPopoverOpen"
    ref="popoverRef"
    v-on-click-outside="[
      () => isPopoverOpen && dismissFlapout(),
      { ignore: ['.vc-chrome', '.colorpicker--chrome'] },
    ]"
    class="fixed z-50 w-[23.75rem] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-4"
    :style="{
      top: `${popoverPosition.top}px`,
      left: `${popoverPosition.left}px`,
    }"
  >
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{
          editingSystemStage
            ? t('PLANNING.STATUSES.FORM.EDIT_SYSTEM_TITLE')
            : editingStatusId
              ? t('PLANNING.STATUSES.FORM.EDIT_TITLE')
              : t('PLANNING.STATUSES.FORM.CREATE_TITLE')
        }}
      </h3>
    </div>

    <div class="space-y-4">
      <template v-if="editingSystemStage">
        <p class="text-sm font-medium text-n-slate-12">
          {{ formatStageLabel(editingSystemStage) }}
        </p>
        <div>
          <p class="text-xs font-medium text-n-slate-11 mb-2">
            {{ t('PLANNING.STATUSES.COLOR.LABEL') }}
          </p>
          <StatusColorPicker
            :model-value="form.color"
            :fallback-color="getDefaultStageColor(editingSystemStage)"
            :reset-label="t('PLANNING.STATUSES.COLOR.RESET_SYSTEM')"
            @update:model-value="form.color = $event"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('PLANNING.STATUSES.COLOR.SYSTEM_HINT') }}
          </p>
        </div>
      </template>
      <template v-else>
        <input
          v-model="form.name"
          type="text"
          :placeholder="t('PLANNING.STATUSES.FORM.NAME_PLACEHOLDER')"
          class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800"
        />
        <select
          v-model="form.anchor_system_stage"
          class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800"
        >
          <option v-for="stage in systemStages" :key="stage" :value="stage">
            {{ formatStageLabel(stage) }}
          </option>
        </select>
        <p class="text-xs text-n-slate-11">
          {{ t('PLANNING.STATUSES.FORM.STAGE_HINT') }}
        </p>
        <div>
          <p class="text-xs font-medium text-n-slate-11 mb-2">
            {{ t('PLANNING.STATUSES.COLOR.LABEL') }}
          </p>
          <StatusColorPicker
            :model-value="form.color"
            :fallback-color="systemResolvedColor(form.anchor_system_stage)"
            :reset-label="t('PLANNING.STATUSES.COLOR.RESET_CUSTOM')"
            @update:model-value="form.color = $event"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('PLANNING.STATUSES.COLOR.INHERIT_HINT') }}
          </p>
        </div>
        <div class="flex items-center gap-3 py-2">
          <Switch v-model="form.active" />
          <span class="text-sm font-semibold text-n-slate-12">
            {{ t('PLANNING.STATUSES.FORM.ACTIVE_LABEL') }}
          </span>
        </div>
      </template>
    </div>

    <div class="mt-4 flex items-center gap-2">
      <Button
        :label="t('PLANNING.STATUSES.FORM.CANCEL')"
        slate
        outlined
        class="w-full"
        @click="cancelFlapout"
      />
      <Button
        :label="
          editingStatusId || editingSystemStage
            ? t('PLANNING.STATUSES.FORM.SAVE')
            : t('PLANNING.STATUSES.FORM.CREATE')
        "
        class="w-full"
        @click="saveStatus"
      />
    </div>
  </div>

  <div
    v-if="showDeleteModal"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="closeDeleteModal"
  >
    <div
      class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-2xl p-6 mx-4"
    >
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ t('PLANNING.STATUSES.DELETE_MODAL.TITLE') }}
      </h3>
      <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {{ t('PLANNING.STATUSES.DELETE_MODAL.DESCRIPTION_PRE') }}
        <span class="font-semibold">{{ selectedStatus?.name }}</span>
        {{ t('PLANNING.STATUSES.DELETE_MODAL.DESCRIPTION_POST') }}
      </p>
      <div class="mt-5 flex items-center gap-2">
        <Button
          :label="t('PLANNING.STATUSES.DELETE_MODAL.CANCEL')"
          slate
          outlined
          class="w-full"
          @click="closeDeleteModal"
        />
        <Button
          :label="t('PLANNING.STATUSES.DELETE_MODAL.CONFIRM')"
          ruby
          class="w-full"
          @click="removeStatus"
        />
      </div>
    </div>
  </div>

  <EventReminderModal
    v-model="showReminderModal"
    :status="reminderModalStatus"
    @update:model-value="val => !val && onReminderModalClose()"
  />

  <EventStageDefaultsModal v-model="showStageDefaultsModal" />
</template>
